import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import type { ListResponse, Me, Overview, Plan, PlansResponse, SubscriptionsResponse, TeamDetail, TeamSummary, UserItem, Invoice } from './types'

export type Params = Record<string, string | undefined>

const clean = (params: Params) => Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''))

function useList<T, R = ListResponse<T>>(key: string, path: string, params: Params, enabled = true) {
  const query = clean(params)
  return useQuery({
    queryKey: [key, query],
    queryFn: async () => (await api.get<R>(path, { params: query })).data,
    placeholderData: keepPreviousData,
    enabled,
  })
}

/** Who the API thinks you are. Only asked once Firebase has a signed-in user, or the request goes out without a token. */
export const useMe = (signedIn: boolean) =>
  useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get<Me>('/me')).data,
    enabled: signedIn,
    retry: false,
    staleTime: 5 * 60_000,
  })

export const useOverview = () =>
  useQuery({ queryKey: ['overview'], queryFn: async () => (await api.get<Overview>('/overview')).data })

export const useTeams = (params: Params, enabled = true) => useList<TeamSummary>('teams', '/teams', params, enabled)
export const useUsers = (params: Params, enabled = true) => useList<UserItem>('users', '/users', params, enabled)
export const useInvoices = (params: Params) => useList<Invoice>('invoices', '/invoices', params)
export const useSubscriptions = (params: Params) => useList<TeamSummary, SubscriptionsResponse>('subscriptions', '/subscriptions', params)

export const useTeamDetail = (id: string | undefined) =>
  useQuery({
    queryKey: ['team', id],
    queryFn: async () => (await api.get<TeamDetail>(`/teams/${id}`)).data,
    enabled: !!id,
  })

export const usePlanList = () =>
  useQuery({ queryKey: ['plans'], queryFn: async () => (await api.get<PlansResponse>('/plans')).data })

export const usePlans = () => {
  const q = usePlanList()
  return { ...q, data: q.data?.plans }
}

export type PlanEdit = Partial<Pick<Plan, 'name' | 'price_cents' | 'features' | 'limits' | 'public' | 'sort' | 'stripe_price_id'>>

/** Saves changes to one plan, then reloads whatever shows plans. */
export function useUpdatePlan() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, changes }: { id: string; changes: PlanEdit }) => (await api.put<Plan>(`/plans/${id}`, changes)).data,
    onSuccess: () => client.invalidateQueries(),
  })
}

export interface SetPlanInput {
  teamId: number
  planId: string
  /** ISO time; null keeps the plan until someone changes it. */
  expiresAt: string | null
  note: string
  force: boolean
}

/** Puts a team on a plan by hand, then reloads whatever shows plans. */
export function useSetTeamPlan() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (v: SetPlanInput) =>
      (await api.put(`/teams/${v.teamId}/plan`, { plan_id: v.planId, expires_at: v.expiresAt, note: v.note, force: v.force })).data,
    onSuccess: () => client.invalidateQueries(),
  })
}

/** Asks the API to re-read the Firebase user list, then reloads whatever is on screen. */
export function useRefreshAll() {
  const client = useQueryClient()
  return async () => {
    await api.get('/overview', { params: { refresh: 1 } })
    await client.invalidateQueries()
  }
}
